import { useCallback, useEffect, useMemo, useState } from 'react';

import { getSkillPathError, SKILL_MARKDOWN_PATH, SKILL_PATH_KINDS } from '@aline/common';
import { useRichTreeViewProApiRef } from '@mui/x-tree-view-pro';
import { useSnackbar } from 'notistack';
import { useLatest } from 'react-use';
import { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';
import { getErrorMessage } from './skill-editor-errors';
import {
  getIsSkillPathTaken,
  getNewFileLimitError,
  getSavedTextFileEdit,
  getSelectedTextFile,
  getShownSectionId,
  getSkillTreeItemPathKind,
} from './skill-file-rules';
import {
  buildSkillFileTree,
  getSkillDocumentItems,
  getSkillTreeFolderPaths,
  getSkillTreeItemContentType,
  getSkillTreeMoveName,
  getSkillTreeSubtreeIds,
} from './skill-file-tree';
import type { SkillTreeDragArea } from './skill-tree-drag';
import {
  formatSkillTreeItemId,
  getSkillAncestorFolderTreeIds,
  INSTRUCTIONS_TREE_ITEM,
  parseSkillTreeItemId,
} from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';
import type { SkillMoveTarget, SkillTreeActions, SkillTreeFileState } from './skill-tree-item';
import {
  findPendingTreeItem,
  followMovedSkillPath,
  getIsRelocatableSkillTreeItem,
  getIsSkillPathInside,
  getParentPath,
  getPendingTreeTarget,
  joinSkillPath,
  relocateSkillAttachmentChanges,
  relocateSkillPath,
  relocateSkillTreeItem,
  relocateSkillTreeItemId,
} from './skill-tree-relocation';
import type {
  PendingTreeTarget,
  RelocatableSkillTreeItem,
  SkillTreeRelocation,
} from './skill-tree-relocation';
import { useTreeNameInput } from './use-tree-name-input';

/**
 * A change to the file tree asked for while a save runs, applied once the save lands. The save
 * turns new files into saved ones, and changing a file it is storing would apply to both, so
 * the change waits and finds its item again afterwards.
 */
type PendingTreeChange =
  | { folder: string; kind: 'move'; target: PendingTreeTarget }
  | { kind: 'rename'; name: string; target: PendingTreeTarget }
  | { kind: 'remove'; target: PendingTreeTarget };

type TreeRename =
  | { error: string; relocation: null }
  | { error: null; relocation: SkillTreeRelocation | null };

export type SkillTreeSelection = ReturnType<typeof useSkillTreeSelection>;
export type SkillFileTree = ReturnType<typeof useSkillFileTree>;

/**
 * What the editor shows and which folders are open. Kept apart from the rest of the tree
 * because loading and saving the draft move the selection too.
 */
export function useSkillTreeSelection() {
  const [section, setSection] = useState<SkillTreeItem>(INSTRUCTIONS_TREE_ITEM);
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
  return { expandedFolders, section, setExpandedFolders, setSection };
}

/** The skill's file tree: what it shows, and creating, renaming, moving and removing in it. */
export function useSkillFileTree({
  attachmentChanges,
  attachmentChangesRef,
  canEdit,
  editAttachmentChanges,
  flushEditors,
  getIsSaving,
  savedPaths,
  saving,
  selection,
  skill,
  skillId,
}: {
  attachmentChanges: SkillAttachmentChanges;
  attachmentChangesRef: React.RefObject<SkillAttachmentChanges>;
  canEdit: boolean;
  editAttachmentChanges: (update: React.SetStateAction<SkillAttachmentChanges>) => void;
  flushEditors: () => void;
  /** Whether a save is running now. A change asked for meanwhile waits for it. */
  getIsSaving: () => boolean;
  savedPaths: string[];
  saving: boolean;
  selection: SkillTreeSelection;
  skill: Apollo.AgentSkillSimpleFragment | null;
  skillId: string;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const treeApiRef = useRichTreeViewProApiRef();
  const { expandedFolders, section, setExpandedFolders, setSection } = selection;
  const [folders, setFolders] = useState<string[]>([]);
  // The folder a new file or folder is created in.
  const [creationFolder, setCreationFolder] = useState('');
  // The folder the inline input for a new file or folder sits in.
  const [draftFolder, setDraftFolder] = useState('');
  const [creating, setCreating] = useState<'file' | 'folder' | null>(null);
  const [renaming, setRenaming] = useState<RelocatableSkillTreeItem | null>(null);
  const [pendingTreeChanges, setPendingTreeChanges] = useState<PendingTreeChange[]>([]);
  const [retryFile] = Apollo.useRetryAgentSkillFileMutation({ errorPolicy: 'none' });

  const savedAttachments = useMemo(() => skill?.attachments ?? [], [skill?.attachments]);
  const documentItems = useMemo(
    () => getSkillDocumentItems({ attachments: savedAttachments, changes: attachmentChanges }),
    [savedAttachments, attachmentChanges]
  );

  // Text edits.
  /**
   * An edit to a text file. `path` is the one the file editor was opened with: a saved
   * file's stored path until its first edit turns it into an unsaved text file.
   */
  const changeTextFile = useCallback(
    (path: string, text: string) => {
      if (!canEdit) return;
      const current = attachmentChangesRef.current;
      if (current.textFiles?.some(file => file.path === path)) {
        editAttachmentChanges({
          ...current,
          textFiles: current.textFiles.map(file =>
            file.path === path ? { ...file, content: text } : file
          ),
        });
        return;
      }
      const edit = getSavedTextFileEdit({
        attachments: savedAttachments,
        changes: current,
        path,
        savedPaths,
        text,
      });
      if (!edit) return;
      const { changes: edited, newPath } = edit;
      editAttachmentChanges(edited);
      setSection(selected =>
        selected.kind === 'saved' && selected.path === path
          ? { kind: 'text', path: newPath }
          : selected
      );
    },
    [attachmentChangesRef, canEdit, editAttachmentChanges, savedAttachments, savedPaths, setSection]
  );

  // Moving and renaming.
  const relocate = useCallback(
    (relocation: SkillTreeRelocation) => {
      // An edit still in the open file's debounce belongs to the file before it moves.
      flushEditors();
      editAttachmentChanges(
        relocateSkillAttachmentChanges({
          attachments: savedAttachments,
          changes: attachmentChangesRef.current,
          relocation,
        })
      );
      const relocateId = (id: string) => relocateSkillTreeItemId(id, relocation);
      const relocatePath = (path: string) => relocateSkillPath(path, relocation);
      const itemId = formatSkillTreeItemId(relocation.item);
      const oldParent =
        relocation.item.kind === 'folder'
          ? getParentPath(relocation.item.path)
          : (documentItems.find(file => file.id === itemId)?.folder ?? '');
      const newParent = getParentPath(relocation.path);
      setFolders(current => [
        ...new Set([
          ...current.map(relocatePath),
          ...(relocation.item.kind === 'folder' ? [relocation.path] : []),
          // A file moved out of a folder leaves the folder in place, even when it is now empty.
          ...(oldParent && oldParent !== newParent ? [oldParent] : []),
        ]),
      ]);
      setExpandedFolders(current => [
        ...new Set([...current.map(relocateId), ...getSkillAncestorFolderTreeIds(relocation.path)]),
      ]);
      setCreationFolder(current => relocatePath(current));
      setSection(current => relocateSkillTreeItem(current, relocation));
    },
    [
      attachmentChangesRef,
      documentItems,
      editAttachmentChanges,
      flushEditors,
      savedAttachments,
      setExpandedFolders,
      setSection,
    ]
  );

  const getPendingTarget = useCallback(
    (item: RelocatableSkillTreeItem) => getPendingTreeTarget({ documentItems, item }),
    [documentItems]
  );
  const queueTreeChange = useCallback((change: PendingTreeChange) => {
    setPendingTreeChanges(current => [...current, change]);
  }, []);

  /** The folder an item sits in and its name, or null for a file the tree does not show. */
  const getTreeItemPlace = useCallback(
    (item: RelocatableSkillTreeItem) => {
      if (item.kind === 'folder') {
        const name = item.path.split('/').at(-1) ?? '';
        return { folder: getParentPath(item.path), name, pathName: name };
      }
      const itemId = formatSkillTreeItemId(item);
      const file = documentItems.find(entry => entry.id === itemId);
      return file
        ? { folder: file.folder, name: file.label, pathName: getSkillTreeMoveName(file) }
        : null;
    },
    [documentItems]
  );

  /**
   * Renaming `item` to `name`: the relocation, null when the name did not change, or why the
   * name cannot be used.
   */
  const getRename = useCallback(
    (item: RelocatableSkillTreeItem, rawName: string): TreeRename => {
      const name = rawName.trim();
      if (!name || name === '.' || name === '..' || /[\\/]/.test(name))
        return { error: 'Enter a valid name without / or \\.', relocation: null };
      const place = getTreeItemPlace(item) ?? { folder: '', name: '' };
      if (name === place.name) return { error: null, relocation: null };
      const path = joinSkillPath(place.folder, name);
      const error =
        getSkillPathError(path, getSkillTreeItemPathKind(item)) ??
        (getIsSkillPathTaken({ documentItems, folders, path, self: formatSkillTreeItemId(item) })
          ? 'This name is already in use.'
          : null);
      if (error) return { error, relocation: null };
      return { error: null, relocation: { item, name, path } };
    },
    [documentItems, folders, getTreeItemPlace]
  );

  const commitRename = useCallback(
    (name: string) => {
      if (!renaming || !canEdit) return null;
      const { error, relocation } = getRename(renaming, name);
      if (error !== null) return error;
      setRenaming(null);
      if (!relocation) return null;
      if (!getIsSaving()) {
        relocate(relocation);
        return null;
      }
      const target = getPendingTarget(renaming);
      if (target) queueTreeChange({ kind: 'rename', name: relocation.name, target });
      return null;
    },
    [canEdit, getIsSaving, getPendingTarget, getRename, queueTreeChange, relocate, renaming]
  );
  const cancelRename = useCallback(() => setRenaming(null), []);
  const renameName = useTreeNameInput({
    label: 'File or folder name',
    onCancel: cancelRename,
    onCommit: commitRename,
  });
  const { start: startRenameName } = renameName;
  const beginRename = useCallback(
    (item: RelocatableSkillTreeItem, name: string) => {
      setRenaming(item);
      startRenameName(name);
    },
    [startRenameName]
  );

  const removeTreeItem = useCallback(
    (item: RelocatableSkillTreeItem) => {
      if (!canEdit) return;
      if (getIsSaving()) {
        const target = getPendingTarget(item);
        if (target) queueTreeChange({ kind: 'remove', target });
        return;
      }
      const itemId = formatSkillTreeItemId(item);
      const folder = item.kind === 'folder' ? item.path : null;
      const removedIds = new Set(
        documentItems
          .filter(
            file =>
              file.id === itemId || (folder !== null && getIsSkillPathInside(file.folder, folder))
          )
          .map(file => file.id)
      );
      const isRemoved = (removed: SkillTreeItem) => removedIds.has(formatSkillTreeItemId(removed));
      // An empty folder only exists in the tree, so removing it changes no file.
      if (removedIds.size)
        editAttachmentChanges(current => ({
          ...current,
          attachmentRenames: current.attachmentRenames?.filter(
            file => !isRemoved({ kind: 'saved', path: file.path })
          ),
          documentSnapshots: current.documentSnapshots?.filter(
            file => !isRemoved({ documentId: file.documentId, kind: 'snapshot' })
          ),
          pdfAttachments: current.pdfAttachments?.filter(
            file => !isRemoved({ id: file.id, kind: 'pdf' })
          ),
          retainedAttachmentPaths: (current.retainedAttachmentPaths ?? savedPaths).filter(
            path => !isRemoved({ kind: 'saved', path })
          ),
          textFiles: current.textFiles?.filter(
            file => !isRemoved({ kind: 'text', path: file.path })
          ),
        }));
      if (folder !== null) {
        setFolders(current => current.filter(path => !getIsSkillPathInside(path, folder)));
        setExpandedFolders(current =>
          current.filter(id => {
            const expanded = parseSkillTreeItemId(id);
            return expanded?.kind !== 'folder' || !getIsSkillPathInside(expanded.path, folder);
          })
        );
        setCreationFolder('');
      }
      setSection(current => (isRemoved(current) ? INSTRUCTIONS_TREE_ITEM : current));
    },
    [
      canEdit,
      documentItems,
      editAttachmentChanges,
      getIsSaving,
      getPendingTarget,
      queueTreeChange,
      savedPaths,
      setExpandedFolders,
      setSection,
    ]
  );
  const getFolderHasFiles = useCallback(
    (path: string) => documentItems.some(file => getIsSkillPathInside(file.folder, path)),
    [documentItems]
  );

  const expandFolder = useCallback(
    (path: string) => {
      if (!path) return;
      setExpandedFolders(current => [
        ...new Set([
          ...current,
          ...getSkillAncestorFolderTreeIds(path),
          formatSkillTreeItemId({ kind: 'folder', path }),
        ]),
      ]);
    },
    [setExpandedFolders]
  );
  const getIsFolderExpanded = useCallback(
    (path: string) => expandedFolders.includes(formatSkillTreeItemId({ kind: 'folder', path })),
    [expandedFolders]
  );
  const changeExpandedFolders = useCallback(
    (_event: React.SyntheticEvent | null, ids: string[]) => setExpandedFolders(ids),
    [setExpandedFolders]
  );

  /** Why the item cannot move into the folder ('' for the top level), or null when it can. */
  const getMoveError = useCallback(
    (item: RelocatableSkillTreeItem, folder: string): string | null => {
      const place = getTreeItemPlace(item);
      if (!place?.pathName) return 'This item cannot be moved.';
      if (folder === place.folder) return 'Current folder';
      if (item.kind === 'folder' && getIsSkillPathInside(folder, item.path))
        return 'A folder cannot go inside itself.';
      const path = joinSkillPath(folder, place.pathName);
      return (
        getSkillPathError(path, getSkillTreeItemPathKind(item)) ??
        (getIsSkillPathTaken({ documentItems, folders, path, self: formatSkillTreeItemId(item) })
          ? 'Something here already has this name.'
          : null)
      );
    },
    [documentItems, folders, getTreeItemPlace]
  );
  /** Move a file or folder into another folder and show it there. Returns the move made. */
  const moveTreeItem = useCallback(
    (item: RelocatableSkillTreeItem, folder: string): SkillTreeRelocation | null => {
      const place = getTreeItemPlace(item);
      if (!canEdit || !place?.pathName || getMoveError(item, folder)) return null;
      if (getIsSaving()) {
        const target = getPendingTarget(item);
        if (target) queueTreeChange({ folder, kind: 'move', target });
        return null;
      }
      const relocation = {
        item,
        name: place.name,
        path: joinSkillPath(folder, place.pathName),
      };
      relocate(relocation);
      expandFolder(folder);
      if (item.kind === 'folder') setCreationFolder(relocation.path);
      else {
        setCreationFolder('');
        setSection(relocateSkillTreeItem(item, relocation));
      }
      return relocation;
    },
    [
      canEdit,
      expandFolder,
      getIsSaving,
      getMoveError,
      getPendingTarget,
      getTreeItemPlace,
      queueTreeChange,
      relocate,
      setSection,
    ]
  );

  // One change per run: each changes the files the next one is checked against.
  useEffect(() => {
    const [next] = pendingTreeChanges;
    if (!next || saving || getIsSaving()) return;
    const item = findPendingTreeItem({
      attachments: savedAttachments,
      documentItems,
      target: next.target,
    });
    const moved = item ? applyPendingTreeChange(next, item) : null;
    /**
     * A later change can name the same item, or something inside a moved folder, by where it
     * was when it was asked for. It follows the item to where this change put it.
     */
    setPendingTreeChanges(current => {
      const rest = current.slice(1);
      if (!moved) return rest;
      return rest.map(change => {
        const path = followMovedSkillPath({
          from: next.target.path,
          path: change.target.path,
          to: moved.path,
        });
        return { ...change, target: { ...change.target, path } };
      });
    });

    function applyPendingTreeChange(
      change: PendingTreeChange,
      target: RelocatableSkillTreeItem
    ): SkillTreeRelocation | null {
      switch (change.kind) {
        case 'move':
          return moveTreeItem(target, change.folder);
        case 'rename': {
          // Checked again: the files may have changed since the name was typed.
          const { relocation } = getRename(target, change.name);
          if (relocation) relocate(relocation);
          return relocation;
        }
        case 'remove':
          removeTreeItem(target);
          return null;
      }
    }
  }, [
    documentItems,
    getIsSaving,
    getRename,
    moveTreeItem,
    pendingTreeChanges,
    relocate,
    removeTreeItem,
    savedAttachments,
    saving,
  ]);

  // New files and folders.
  const confirmFolder = useCallback(
    (rawName: string) => {
      const name = rawName.trim();
      if (!name) {
        setCreating(null);
        return null;
      }
      const path = joinSkillPath(draftFolder, name);
      const error =
        getSkillPathError(path, SKILL_PATH_KINDS.folder) ??
        (getIsSkillPathTaken({ documentItems, folders, path, self: null })
          ? 'This name is already in use.'
          : null);
      if (error) return error;
      setFolders(current => [...current, path]);
      setCreationFolder(path);
      setCreating(null);
      return null;
    },
    [documentItems, draftFolder, folders]
  );
  const createEntry = useCallback(
    (rawName: string) => {
      const name = rawName.trim();
      if (!name) {
        setCreating(null);
        return null;
      }
      const path = joinSkillPath(draftFolder, name);
      const error =
        getSkillPathError(path, SKILL_PATH_KINDS.file) ??
        (getIsSkillPathTaken({ documentItems, folders, path, self: null })
          ? 'A file with this name already exists.'
          : null) ??
        getNewFileLimitError(documentItems);
      if (error) return error;
      editAttachmentChanges(current => ({
        ...current,
        textFiles: [
          ...(current.textFiles ?? []),
          { content: '', contentType: 'text/markdown', path },
        ],
      }));
      setExpandedFolders(current => [
        ...new Set([...current, ...getSkillAncestorFolderTreeIds(path)]),
      ]);
      setSection({ kind: 'text', path });
      setCreating(null);
      return null;
    },
    [documentItems, draftFolder, editAttachmentChanges, folders, setExpandedFolders, setSection]
  );
  const closeDraft = useCallback(() => setCreating(null), []);
  const draftName = useTreeNameInput({
    label: creating === 'file' ? 'File name' : 'Folder name',
    onCancel: closeDraft,
    onCommit: creating === 'file' ? createEntry : confirmFolder,
  });
  const { start: startDraftName } = draftName;

  // What the tree shows.
  const treeItems = useMemo(
    () =>
      buildSkillFileTree({
        documentItems,
        draft: creating
          ? {
              folder: draftFolder,
              item: creating === 'file' ? { kind: 'fileDraft' } : { kind: 'folderDraft' },
              label: creating === 'file' ? draftName.value : 'New folder',
            }
          : null,
        folders,
      }),
    [creating, documentItems, draftFolder, draftName.value, folders]
  );
  const selectedItemId = getShownSectionId({ changes: attachmentChanges, documentItems, section });
  const selectedItemIdRef = useLatest(selectedItemId);
  const selectedItem = useMemo(
    () => parseSkillTreeItemId(selectedItemId) ?? INSTRUCTIONS_TREE_ITEM,
    [selectedItemId]
  );
  const selectedName =
    selectedItem.kind === 'instructions'
      ? SKILL_MARKDOWN_PATH
      : (documentItems.find(item => item.id === selectedItemId)?.label ?? '');
  const selectedContentType = getSkillTreeItemContentType(documentItems, selectedItemId);
  const selectedTextFile = useMemo(
    () =>
      getSelectedTextFile({
        attachments: savedAttachments,
        changes: attachmentChanges,
        selectedItem,
      }),
    [savedAttachments, attachmentChanges, selectedItem]
  );

  /** A new file or folder goes in the selected folder, or else in the selected file's. */
  const getNewItemFolder = useCallback(
    () => creationFolder || (documentItems.find(item => item.id === selectedItemId)?.folder ?? ''),
    [creationFolder, documentItems, selectedItemId]
  );
  const openDraft = useCallback(
    (kind: 'file' | 'folder') => {
      const folder = getNewItemFolder();
      setDraftFolder(folder);
      expandFolder(folder);
      startDraftName(kind === 'file' ? 'New file.md' : 'New folder');
      setCreating(kind);
    },
    [expandFolder, getNewItemFolder, startDraftName]
  );
  const openNewFile = useCallback(() => openDraft('file'), [openDraft]);
  const openNewFolder = useCallback(() => openDraft('folder'), [openDraft]);
  /**
   * Add a top-level folder by name, for the conventional ones the spec suggests. Folders are
   * optional and exist only through the files in them, so an unused one is dropped on save.
   */
  const addSuggestedFolder = useCallback(
    (name: string) => {
      const exists =
        folders.includes(name) || documentItems.some(file => file.folder.split('/')[0] === name);
      if (!exists) setFolders(current => [...current, name]);
      setCreationFolder(name);
      expandFolder(name);
    },
    [documentItems, expandFolder, folders]
  );

  // Selection and focus.
  /**
   * The tree view moves focus to a neighbor row when the focused row goes away, and a move or
   * a save gives the moved file a new row, so the neighbor looked selected. Focus goes back to
   * the selected file instead. Its row is known to the tree already but not mounted yet, so
   * the tree's focus moves now, before the neighbor renders as focused, and the browser's
   * once the row has mounted.
   */
  const handleTreeItemFocus = useCallback(
    (event: React.SyntheticEvent | null, itemId: string) => {
      // Only the tree's own fallback comes without an event; focus the author moves stays.
      if (event || itemId === selectedItemIdRef.current) return;
      const focusSelected = () => treeApiRef.current?.focusItem(null, selectedItemIdRef.current);
      focusSelected();
      queueMicrotask(focusSelected);
    },
    [selectedItemIdRef, treeApiRef]
  );
  const handleTreeSelection = useCallback(
    (_event: React.SyntheticEvent | null, itemId: string | null) => {
      const item = itemId ? parseSkillTreeItemId(itemId) : null;
      if (!item || item.kind === 'folderDraft' || item.kind === 'fileDraft') return;
      if (item.kind === 'folder') {
        setCreationFolder(item.path);
        return;
      }
      setCreationFolder('');
      setSection(item);
    },
    [setSection]
  );
  const selectReplacedCopy = useCallback(
    (documentId: string) => setSection({ documentId, kind: 'snapshot' }),
    [setSection]
  );

  // Dragging.
  const getMoveTargets = useCallback(
    (item: RelocatableSkillTreeItem): SkillMoveTarget[] => [
      { depth: 0, error: getMoveError(item, ''), folder: '', label: 'Top level' },
      ...getSkillTreeFolderPaths(treeItems)
        .filter(path => item.kind !== 'folder' || !getIsSkillPathInside(path, item.path))
        .map(path => ({
          depth: path.split('/').length,
          error: getMoveError(item, path),
          folder: path,
          label: path.split('/').at(-1) ?? path,
        })),
    ],
    [getMoveError, treeItems]
  );
  const dragProps = useMemo<Omit<React.ComponentProps<typeof SkillTreeDragArea>, 'children'>>(
    () => ({
      canMove: (itemId, folder) => {
        const item = parseSkillTreeItemId(itemId);
        return Boolean(item && getIsRelocatableSkillTreeItem(item) && !getMoveError(item, folder));
      },
      expandFolder,
      getContentType: itemId => getSkillTreeItemContentType(documentItems, itemId),
      getDropFolder: itemId => {
        const item = parseSkillTreeItemId(itemId);
        if (item?.kind === 'instructions') return '';
        if (!item || !getIsRelocatableSkillTreeItem(item)) return null;
        return item.kind === 'folder' ? item.path : (getTreeItemPlace(item)?.folder ?? null);
      },
      getIsFolderExpanded,
      getLabel: itemId => {
        const item = parseSkillTreeItemId(itemId);
        if (!item || !getIsRelocatableSkillTreeItem(item)) return '';
        return getTreeItemPlace(item)?.name ?? '';
      },
      getSubtreeIds: itemId => getSkillTreeSubtreeIds(treeItems, itemId),
      move: (itemId, folder) => {
        const item = parseSkillTreeItemId(itemId);
        if (item && getIsRelocatableSkillTreeItem(item)) moveTreeItem(item, folder);
      },
    }),
    [
      documentItems,
      expandFolder,
      getIsFolderExpanded,
      getMoveError,
      getTreeItemPlace,
      moveTreeItem,
      treeItems,
    ]
  );

  // File processing.
  const retryProcessing = useCallback(
    async (path: string) => {
      try {
        await retryFile({ variables: { input: { id: skillId, path } } });
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Could not retry this file.'), { variant: 'error' });
      }
    },
    [enqueueSnackbar, retryFile, skillId]
  );
  const getFileState = useCallback(
    (itemId: string): SkillTreeFileState | null => {
      const item = parseSkillTreeItemId(itemId);
      if (!canEdit || item?.kind !== 'saved') return null;
      const file = savedAttachments.find(attachment => attachment.path === item.path);
      switch (file?.processing) {
        case undefined:
        case Apollo.AgentSkillFileProcessing.Ready:
          return null;
        case Apollo.AgentSkillFileProcessing.Processing:
          return { kind: 'processing' };
        case Apollo.AgentSkillFileProcessing.Failed:
          return {
            kind: 'failed',
            message: file.processingError ?? 'This file could not be read.',
            path: file.path,
          };
      }
    },
    [canEdit, savedAttachments]
  );

  const treeActions = useMemo<SkillTreeActions>(
    () => ({
      // Changes asked for during a save wait for it, so the row actions and dragging stay on.
      editable: canEdit,
      getContentType: itemId => getSkillTreeItemContentType(documentItems, itemId),
      getFileState,
      getFolderHasFiles,
      getMoveTargets,
      movable: canEdit && !renaming && !creating,
      move: moveTreeItem,
      remove: removeTreeItem,
      rename: beginRename,
      renaming: renaming ? formatSkillTreeItemId(renaming) : null,
      retry: path => void retryProcessing(path),
    }),
    [
      beginRename,
      canEdit,
      creating,
      documentItems,
      getFileState,
      getFolderHasFiles,
      getMoveTargets,
      moveTreeItem,
      removeTreeItem,
      renaming,
      retryProcessing,
    ]
  );

  return {
    addSuggestedFolder,
    changeExpandedFolders,
    changeTextFile,
    creationFolder,
    draftInput: draftName.input,
    dragProps,
    expandedFolders,
    folders,
    handleTreeItemFocus,
    handleTreeSelection,
    /** A name is being typed into the tree, which holds autosave until it is committed. */
    naming: Boolean(renaming) || Boolean(creating),
    openNewFile,
    openNewFolder,
    renameInput: renameName.input,
    selectReplacedCopy,
    selectedContentType,
    selectedItem,
    selectedItemId,
    selectedName,
    selectedTextFile,
    treeActions,
    treeApiRef,
    treeItems,
  };
}
